import { useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { DisplayLine } from './Cbullshitcoding.typing';
import { useCbullshitcodingCodeFileContent } from './useCbullshitcodingCodeFileContent';
import { useCbullshitcodingDisplayLines } from './useCbullshitcodingDisplayLines';
import { useCbullshitcodingPendingStringsFade } from './useCbullshitcodingPendingStringsFade';
import { useCbullshitcodingPropStringsFade } from './useCbullshitcodingPropStringsFade';

interface UseCbullshitcodingContentInput {
  codeFile?: string;
  propStrings?: string[];
  contentKey?: string;
  fadeDurationMs: number;
  trailingLineDropCount: number;
  maxDisplayLineLength?: number;
  trimCommonIndent: boolean;
  replacements?: Record<string, string>;
  hasContentRef: MutableRefObject<boolean>;
  resetTypingState: () => void;
}

interface UseCbullshitcodingContentOutput {
  strings: DisplayLine[];
  isFadingOut: boolean;
  animationResetKey: string | DisplayLine[];
}

export function useCbullshitcodingContent({
  codeFile,
  propStrings,
  contentKey,
  fadeDurationMs,
  trailingLineDropCount,
  maxDisplayLineLength,
  trimCommonIndent,
  replacements,
  hasContentRef,
  resetTypingState,
}: UseCbullshitcodingContentInput): UseCbullshitcodingContentOutput {
  const [loadedStrings, setLoadedStrings] = useState<string[]>([]);
  const [pendingStrings, setPendingStrings] = useState<string[] | null>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const strings = useCbullshitcodingDisplayLines({
    loadedStrings,
    propStrings,
    replacements,
    maxDisplayLineLength,
    trailingLineDropCount,
    trimCommonIndent,
  });
  const contentControls = {
    fadeDurationMs,
    fadeTimeoutRef,
    hasContentRef,
    resetTypingState,
    setIsFadingOut,
    setLoadedStrings,
    setPendingStrings,
  };

  useCbullshitcodingCodeFileContent({ codeFile, ...contentControls });
  useCbullshitcodingPendingStringsFade({
    isFadingOut,
    pendingStrings,
    ...contentControls,
  });
  useCbullshitcodingPropStringsFade({
    codeFile,
    contentKey,
    propStrings,
    ...contentControls,
  });

  return {
    strings,
    isFadingOut,
    animationResetKey: contentKey ?? strings,
  };
}
